import type { PlayerVehicleId } from '../../content/playerVehicles';
import type { Group } from 'three';
import type { TrafficKind, TrafficModel } from '../../content/traffic';
import { bakeVehicle } from './bake';
import { DoorRig } from './doorRig';
import { buildVehicle } from './buildVehicle';
import { fishLivery, paintStripes, privateLivery, pragyaLivery, taxiLivery, trotroLivery, truckLivery } from './liveries';
import type { Paint } from '@accra/shared';
import { buildAboboyaa, buildBarrier } from './smallBuilds';
import { BOX_TRUCK, HIACE_FISH, PRAGYA, SALOON, SPRINTER, SUV, VITZ } from './specs';
import type { VehicleSpec } from './types';

export type PlayerModel = PlayerVehicleId;

export const PLAYER_SPECS: Record<PlayerModel, VehicleSpec> = { sprinter: SPRINTER, hiace: HIACE_FISH, vitz: VITZ };

const templates = new Map<string, Group>();

function build(model: TrafficModel, kind: TrafficKind, variant: number): Group {
  const seed = variant * 7919 + model.length * 104729;
  switch (model) {
    case 'sprinter':
      return buildVehicle(SPRINTER, { livery: trotroLivery(seed, SPRINTER), yellowPlate: true, seed });
    case 'hiace':
      // Half the Fish in traffic wear the plain white/orange look from the photo, half are painted up.
      return buildVehicle(HIACE_FISH, { livery: variant % 2 === 0 ? fishLivery(seed) : trotroLivery(seed, HIACE_FISH), yellowPlate: true, seed });
    case 'taxi':
      return buildVehicle(SALOON, { livery: taxiLivery(seed, SALOON), yellowPlate: true });
    case 'saloon':
      return buildVehicle(SALOON, { livery: privateLivery(seed) });
    case 'suv':
      return buildVehicle(SUV, { livery: privateLivery(seed) });
    case 'truck':
      return buildVehicle(BOX_TRUCK, { livery: truckLivery(seed) });
    case 'pragya':
      return buildVehicle(PRAGYA, { livery: pragyaLivery(seed), yellowPlate: true });
    case 'aboboyaa':
      return buildAboboyaa(seed);
    case 'barrier':
      return buildBarrier(kind.width, kind.height);
  }
}

/** Traffic meshes are built once per (kind, variant) and cloned; clones share geometry and textures. */
export function trafficMesh(kind: TrafficKind, variant: number): Group {
  const key = `${kind.id}:${variant}`;
  let template = templates.get(key);
  if (!template) {
    template = bakeVehicle(build(kind.id, kind, variant));
    templates.set(key, template);
  }
  return template.clone();
}

/** The player's trotro in its garage paint and sticker. The mate and sliding door are a separate rig so they can animate at stops. */
export function playerTrotro(model: PlayerModel, sticker: string, paint: Paint): { body: Group; door: DoorRig } {
  const spec = PLAYER_SPECS[model];
  const base = trotroLivery(11, spec);
  const livery = { ...base, body: paint.body, stripes: paintStripes(spec, paint.stripes), rearText: sticker, sideText: model === 'sprinter' ? sticker : undefined };
  const body = bakeVehicle(buildVehicle(spec, { livery, yellowPlate: true, seed: 5 }));
  // The Vitz is a small car: no mate hanging out of the door.
  const door = new DoorRig(spec, livery.body, model !== 'vitz');
  body.add(door.group);
  return { body, door };
}
